"use client";

import { useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Container, Section, SectionHeader } from "@/components/ui/Container";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { ListingHero } from "./ListingHero";
import { Pager } from "@/components/ui/Pager";
import { ModuleGrid } from "@/components/modules/ModuleCard";
import { ModuleList } from "@/components/modules/ModuleList";
import { ViewToggle, type ViewMode } from "./ViewToggle";
import { CategoryPills } from "./CategoryPills";
import { FeaturedEditorial, FeaturedSpotlight, FeaturedMosaic } from "./FeaturedEditorial";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { cn } from "@/components/ui/cn";
import { WorkspaceHero } from "@/components/ui/WorkspaceHeading";
import { EmptyState } from "@/components/ui/EmptyState";
import { BookOpenText, CalendarDays, LayoutGrid, Mic, PlaySquare } from "lucide-react";
import type { Category, Module } from "@/lib/lecturehub/types";

/**
 * The module types that have a listing route. Deliberately narrower than
 * `RefType` (= the generated `ModuleRefTypeEnum`): the enum also carries
 * `"webinar"`, which the public scoping never returns, and `"post"`, which is
 * served by the editorial post routes, not a listing. Assigning this into a
 * `ModuleFilterInput.refType` is what keeps it honest against the enum.
 */
export type ListingRefType = "course" | "event" | "ott" | "podcast";

// View preference lives in a cookie so the server renders the chosen view —
// no grid→list flip on load (and it stays sticky across listing pages because
// the server reads it on each navigation).
const VIEW_COOKIE = "lh.listingView";

// 12 per page — the media.n24.si reference listing's page size (4 clean rows
// of 3 on the public grid), down from 24, which most catalogs never exceeded,
// so the pager never appeared.
const PAGE_SIZE = 12;

export function ListingClient({
  lang,
  title,
  tagline,
  basePath,
  refType,
  featuredLimit,
  featuredLayout = "grid",
  emptyMessage,
  categories,
  featuredAll,
  allModules,
  enrolmentIds,
  appMode = false,
  cfHash = null,
  initialView,
  hideHero = false,
}: {
  lang: Lang;
  title: string;
  tagline?: string;
  basePath: string;
  refType: ListingRefType;
  featuredLimit: number;
  /** Guest Featured band arrangement (see FeaturedEditorial): "editorial" is
   *  lead + rail, "spotlight" a single full-width banner, "mosaic" lead + poster
   *  tiles. Absent / "grid" → the ribbon grid, as ever. */
  featuredLayout?: "grid" | "editorial" | "spotlight" | "mosaic";
  emptyMessage?: string;
  categories: Category[];
  featuredAll: Module[];
  allModules: Module[];
  enrolmentIds: string[] | null;
  appMode?: boolean;
  cfHash?: string | null;
  initialView: ViewMode;
  /** Suppress the GUEST hero (ListingHero + breadcrumb) — used when a page
   *  supplies its own header section (a PageHeader block). The logged-in
   *  `appMode` hero is unaffected. */
  hideHero?: boolean;
}) {
  const t = useDict();
  const sp = useSearchParams();
  // Captured once per mount. Event upcoming/past bucketing tolerates an
  // hour of drift; refreshing it would cause needless re-bucketing.
  const [nowMs] = useState(() => Date.now());
  const page = Math.max(1, Number(sp.get("page") ?? "1") || 1);
  const categorySlug = sp.get("category") ?? undefined;

  // The server already read the view cookie and passed it as initialView, so
  // SSR renders the chosen view — no flip on hydration. Toggling updates state
  // and rewrites the cookie for subsequent navigations.
  const [view, setView] = useState<ViewMode>(initialView);
  function changeView(next: ViewMode) {
    setView(next);
    try {
      document.cookie = `${VIEW_COOKIE}=${next};path=/;max-age=31536000;samesite=lax`;
    } catch {
      /* noop */
    }
  }

  // Mirror the searchParams as a plain object for the Pager / CategoryPills
  // which expect the legacy server-side shape.
  const searchParamsObj = useMemo(() => {
    const out: Record<string, string | undefined> = {};
    sp.forEach((v, k) => (out[k] = v));
    return out;
  }, [sp]);

  const enrolledIds = enrolmentIds ? new Set(enrolmentIds) : undefined;

  // Q_MODULES is now refType-scoped server-side (B8), so allModules/featuredAll
  // already match; the .filter here is a cheap defensive no-op. The real work is
  // the event upcoming/past split + client-side pagination below.
  const partitioned = useMemo(() => {
    const allMatching = allModules.filter((m) => m.refType === refType);
    const allFeatured = featuredAll.filter((m) => m.refType === refType).slice(0, featuredLimit);

    let sorted = allMatching;
    if (refType === "event") {
      const now = nowMs;
      const withTime = sorted.map((m) => ({
        m,
        from: m.event?.dateFrom ? new Date(m.event.dateFrom).getTime() : 0,
        past: m.event?.dateTo ? new Date(m.event.dateTo).getTime() < now : false,
      }));
      const upcoming = withTime.filter((x) => !x.past).sort((a, b) => a.from - b.from);
      const past = withTime.filter((x) => x.past).sort((a, b) => b.from - a.from);
      sorted = [...upcoming.map((x) => x.m), ...past.map((x) => x.m)];
    }

    const total = sorted.length;
    const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, lastPage);
    const gridSlice = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    return {
      total,
      lastPage,
      safePage,
      gridSlice,
      allFeatured,
    };
  }, [allModules, featuredAll, refType, featuredLimit, page, nowMs]);

  const empty =
    emptyMessage ??
    (refType === "course"
      ? t.listing.coursesEmpty
      : refType === "event"
        ? t.listing.eventsEmpty
        : refType === "ott"
          ? t.listing.ottsEmpty
          : refType === "podcast"
            ? t.listing.podcastsEmpty
            : t.listing.noResults);

  const gridCols = appMode ? 4 : 3;

  const renderList = (modules: Module[], emptyState?: React.ReactNode) =>
    view === "list" ? (
      <ModuleList
        modules={modules}
        lang={lang}
        t={t}
        enrolledIds={enrolledIds}
        emptyState={emptyState}
        cfHash={cfHash}
        library={appMode}
      />
    ) : (
      <ModuleGrid
        modules={modules}
        lang={lang}
        t={t}
        enrolledIds={enrolledIds}
        cols={gridCols}
        emptyState={emptyState}
        cfHash={cfHash}
        library={appMode}
      />
    );

  return (
    <>
      {appMode ? (
        <WorkspaceHero
          crumbs={[
            { href: langPath(lang, "/dashboard"), label: t.appNav.dashboard },
            { label: title },
          ]}
          eyebrow={t.listing.libraryExplore}
          title={title}
        />
      ) : hideHero ? null : (
        <>
          <ListingHero title={title} tagline={tagline} />

          <section>
            <Container className="pt-5">
              <Breadcrumb
                items={[{ href: langPath(lang, "/"), label: t.nav.home }, { label: title }]}
              />
            </Container>
          </section>
        </>
      )}

      <Section className="!pt-5 animate-in fade-in slide-in-from-bottom-3 duration-500 [animation-timing-function:var(--ease-smooth)]">
        <Container>
          {partitioned.allFeatured.length > 0 ? (
            <div className="mb-12">
              <SectionHeader title={t.listing.featured} />
              {featuredLayout === "editorial" ? (
                <FeaturedEditorial
                  modules={partitioned.allFeatured}
                  lang={lang}
                  t={t}
                  cfHash={cfHash}
                />
              ) : featuredLayout === "spotlight" ? (
                <FeaturedSpotlight
                  modules={partitioned.allFeatured}
                  lang={lang}
                  t={t}
                  cfHash={cfHash}
                />
              ) : featuredLayout === "mosaic" ? (
                <FeaturedMosaic
                  modules={partitioned.allFeatured}
                  lang={lang}
                  t={t}
                  cfHash={cfHash}
                />
              ) : (
                renderList(partitioned.allFeatured)
              )}
            </div>
          ) : null}

          {/* Catalog controls sit with the LIST they act on — below the Featured
              band, not above it: the category pills filter the grid/list, and
              the view toggle switches the grid/list, neither touches Featured.
              With a Featured band the "All <type>" header carries the toggle as
              its action and the pills follow; without one (filtered view, or
              nothing featured) pills and toggle share a single row. */}
          {!appMode && partitioned.allFeatured.length > 0 ? (
            <SectionHeader
              title={`${t.listing.all} ${title.toLowerCase()}`}
              action={
                partitioned.total > 0 ? <ViewToggle value={view} onChange={changeView} /> : null
              }
            />
          ) : null}
          {categories.length > 0 || partitioned.total > 0 ? (
            <div
              className={cn(
                "mb-8 md:mb-10 flex flex-col gap-4 md:flex-row md:items-start md:justify-between",
                // The header above already placed the toggle; pull the pills up
                // under it so the two read as one control block.
                !appMode && partitioned.allFeatured.length > 0 && "-mt-3",
              )}
            >
              <div className="min-w-0 flex-1">
                {categories.length > 0 ? (
                  <CategoryPills
                    categories={categories}
                    current={categorySlug}
                    basePath={basePath}
                    preserveParams={searchParamsObj}
                  />
                ) : null}
              </div>
              {partitioned.total > 0 && (appMode || partitioned.allFeatured.length === 0) ? (
                <div className="shrink-0 self-start md:self-auto">
                  <ViewToggle value={view} onChange={changeView} />
                </div>
              ) : null}
            </div>
          ) : null}
          {/* All published modules (incl. events) render in one grid. Events
              are NOT split into upcoming/archive by date — publish status is
              what gates visibility; the event date is just when it was
              organized. */}
          {/* The one empty-state pattern (/saved, /enrolled, news, documents):
              icon, line, a way out. This was a sentence in a muted box with a
              full-reload anchor. A filter in force means "no matches here", and
              the way out is clearing it; an empty catalog has no way out. */}
          {renderList(
            partitioned.gridSlice,
            <EmptyState
              Icon={
                refType === "course"
                  ? BookOpenText
                  : refType === "event"
                    ? CalendarDays
                    : refType === "ott"
                      ? PlaySquare
                      : refType === "podcast"
                        ? Mic
                        : LayoutGrid
              }
              title={categorySlug ? t.listing.noResults : empty}
              cta={categorySlug ? { href: basePath, label: t.listing.clearFilters } : undefined}
            />,
          )}

          <Pager
            currentPage={partitioned.safePage}
            lastPage={partitioned.lastPage}
            basePath={basePath}
            searchParams={searchParamsObj}
            labels={{ previous: t.listing.previous, next: t.listing.next }}
          />
        </Container>
      </Section>
    </>
  );
}
